import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import * as PageProductStore from '@Stores/PageProduct';
import * as Configurator from '@Stores/Configurator';
import { useDeps } from '@Contexts/DI';
import ApiProduct from '@Api/Product';
import useSearchParams from '@Hooks/useSearchParams';
import queryStringToObject from './helpers/queryStringToObject';
import filterInStock from './helpers/filterInStock';
import useInStock from './useInStock';
import useKeys from './useKeys';

import type { ConfiguratorData } from '@Pages/PageProduct';
import type { FilterResultData } from '@Api/Configurator/getFilterResult';

const useConfigurator = () => {
  const queryClient = useQueryClient();
  const page = PageProductStore.useData();
  const { cylindo, parameters, gallery, credit, isConfigurator, promoCode } = page;
  const { breadcrumbs, name, price, id } = page.product;
  const searchParams = useSearchParams();
  const { logger } = useDeps();
  const selectedOptions = searchParams.toString()
    ? queryStringToObject(searchParams.toString())
    : {};
  const inStock = useInStock();
  const { configurator: queryKey, filters: filterKeys } = useKeys();
  const filters = queryClient.getQueryData<FilterResultData>(filterKeys);
  const cache = queryClient.getQueryData<ConfiguratorData>(queryKey);

  const initialData = useMemo<ConfiguratorData>(
    () => ({
      tabs: [],
      configuration: {
        breadcrumbs,
        cylindo,
        parameters,
        selectedOptions: [],
        parameterValues: [],
        image: gallery[0]?.src || null,
        images: gallery,
        name,
        price,
        credit,
        url: null,
        id,
        isBasic: true,
        isCustom: false,
        isNotModify: true,
        promoCode,
      },
    }),
    [breadcrumbs, cylindo, parameters, gallery, name, price, credit, id, promoCode],
  );

  const updateImage = (responseImage: string) => {
    if (responseImage) {
      const updatedImages = [...initialData.configuration.images];
      updatedImages[0].src = responseImage;
      return updatedImages;
    }
    return initialData.configuration.images;
  };

  const queryFn = async () => {
    if (cache) return cache;

    try {
      const response = await ApiProduct.getConfigurator({ shopProductId: id, selectedOptions });

      if (!response) return initialData;

      Configurator.set(response.configuration?.selectedOptions || []);

      const images = updateImage(response.configuration?.image);

      const configuration = {
        ...initialData.configuration,
        ...response.configuration,
        images,
        isNotModify: !response.configuration,
        promoCode:
          response.configuration.isBasic && !response.configuration.isCustom
            ? promoCode
            : response.configuration.promoCode,
      };

      return { ...response, configuration };
    } catch (err) {
      logger.error(err);
      return initialData;
    }
  };

  const { data, error, isFetching } = useQuery({
    queryKey,
    queryFn,
    placeholderData: (previousData) => previousData || initialData,
    staleTime: 0,
    gcTime: 0,
    enabled: true,
  });

  const sourceData = useMemo(
    () => (isConfigurator && data ? data : initialData),
    [isConfigurator, data, initialData],
  );

  return {
    data: {
      ...sourceData,
      tabs: filterInStock({
        tabs: sourceData.tabs,
        currentInStock: true,
        filters,
        inStock,
        isBasic: sourceData.configuration.isBasic,
      }),
    },
    error,
    isFetching,
  };
};

export default useConfigurator;
